import React, { useState, useEffect, useMemo } from "react";
import { jwtDecode } from "jwt-decode";
import get from "lodash/get";
import {
  getInvoicesByPracticeNumbers,
  // …add your other dataset fetchers here
} from "../afinanceapi";
import { Breadcrumb } from '../Components/Breadcrumb/Breadcrumb';

/**
 * Recursively flattens plain objects (no arrays) so that
 * nested fields become e.g. "practitioner.firstName"
 */
function flattenObject(obj, prefix = "", res = {}) {
  for (const key in obj) {
    const value = obj[key];
    const name = prefix ? `${prefix}.${key}` : key;
    if (value !== null && typeof value === "object" && !Array.isArray(value)) {
      flattenObject(value, name, res);
    } else {
      res[name] = value;
    }
  }
  return res;
}

export function Reports() {
  // ─── state ────────────────────────────────────────────────
  const [dataset, setDataset] = useState("Invoices");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  // pivot‑table controls
  const [rowField, setRowField] = useState("");
  const [valueFields, setValueFields] = useState([]);

  // ─── which fetch to call ──────────────────────────────────
  const fetchers = {
    Invoices: getInvoicesByPracticeNumbers,
    // …etc
  };

  // ─── load data when dataset changes ──────────────────────
  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        const token = sessionStorage.getItem("user");
        if (!token) throw new Error("No token");
        const { practice: practiceNums } = jwtDecode(token);
        const res = await fetchers[dataset](practiceNums);
        setData(res || []);
      } catch (err) {
        console.error("Fetch error:", err);
        setData([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [dataset]);

  // ─── flatten every record once ────────────────────────────
  const flatData = useMemo(() => data.map((d) => flattenObject(d)), [data]);

  // ─── all available field names ───────────────────────────
  const allFields = useMemo(() => {
    if (!flatData.length) return [];
    return Array.from(new Set(flatData.flatMap((d) => Object.keys(d))));
  }, [flatData]);

  // ─── only numeric ones for “values” ──────────────────────
  const numericFields = useMemo(
    () =>
      allFields.filter((f) =>
        flatData.every((d) => typeof d[f] === "number")
      ),
    [allFields, flatData]
  );

  // ─── build the pivot rows ────────────────────────────────
  const pivotRows = useMemo(() => {
    if (!rowField) return [];

    // group by rowField
    const groups = flatData.reduce((acc, row) => {
      const key = get(row, rowField, "⎯");
      acc[key] ||= [];
      acc[key].push(row);
      return acc;
    }, {});

    // shape into an array of { [rowField]: key, Count: n, <value1>: sum, <value2>: sum, … }
    return Object.entries(groups).map(([key, rows]) => {
      const result = { [rowField]: key, Count: rows.length };
      for (let vf of valueFields) {
        result[vf] = rows.reduce((sum, r) => sum + (Number(r[vf]) || 0), 0);
      }
      return result;
    });
  }, [flatData, rowField, valueFields]);

  // ─── render ───────────────────────────────────────────────
  return (
    <div style={{ marginTop: "80px", padding: 16 }}>
      <Breadcrumb items={[
        { label: 'Reports', path: '/reports' },
        { label: dataset },
      ]} />
      <h1>Reports</h1>

      {/* dataset picker */}
      <label>
        Dataset:{" "}
        <select
          value={dataset}
          onChange={(e) => {
            setDataset(e.target.value);
            setRowField("");
            setValueFields([]);
          }}
        >
          {Object.keys(fetchers).map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>
      </label>

      <br />

      {/* row dimension */}
      <label>
        Row Field:{" "}
        <select
          value={rowField}
          onChange={(e) => setRowField(e.target.value)}
        >
          <option value="">-- select field --</option>
          {allFields.map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
        </select>
      </label>

      <br />

      {/* values (multi‑select numeric fields) */}
      <label>
        Values:{" "}
        <select
          multiple
          size={Math.min(6, numericFields.length || 1)}
          value={valueFields}
          onChange={(e) =>
            setValueFields(
              Array.from(e.target.selectedOptions, (opt) => opt.value)
            )
          }
        >
          {numericFields.map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
        </select>
      </label>

      <br />
      {loading && <p>Loading…</p>}

      {/* pivot table */}
      {!loading && pivotRows.length > 0 && (
        <table
          border={1}
          cellPadding={4}
          style={{ borderCollapse: "collapse", marginTop: 16 }}
        >
          <thead>
            <tr>
              <th>{rowField}</th>
              <th>Count</th>
              {valueFields.map((vf) => (
                <th key={vf}>{vf}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pivotRows.map((row, i) => (
              <tr key={i}>
                <td>{row[rowField]}</td>
                <td>{row.Count}</td>
                {valueFields.map((vf) => (
                  <td key={vf}>{row[vf]}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

